import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GatewayLogType, NotificationType, Project, ToolType } from './utils/types';
import { DEFAULT_FONT_SIZE } from './utils/common/constants';
import { findLanguageIndex, StripDoubleQuotes } from './utils/functions';
import { ServiceStatusParams } from './utils/events';

// types

// functions

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

export interface AppState {
  //
  showNewSessionModal: {
    show: boolean;
    site_uuid?: string;
    service_uuid?: string;
    service_type?: string;
  };
  // general:
  selectedTool: ToolType;
  intermediateTool: ToolType;
  languageDropdownMenuDisplayed: boolean;
  language: string | null;
  fontSize: number;
  theme: string;
  userDropdownMenuDisplayed: boolean;
  languageIndex: number | undefined;
  // notebook tool:
  jupyterBaseURL: string;
  jupyterURL: string;
  jupyterRender: number;
  xsrfToken: string | null;
  loadingToken: boolean;
  notifications: NotificationType[];
  sitePanelFilter: string;
  projectPanelFilter: string;
  loadingSites: boolean;
  updateServiceStatus: ServiceStatusParams[];
  gatewayLogs: GatewayLogType[];
  projects: Project[];
  loadingProjects: boolean;
  showPreferencesModal: boolean;
  //
} // AppState

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: AppState = {
  showNewSessionModal: { show: false },
  selectedTool: ToolType.HOME,
  intermediateTool: ToolType.NONE,
  languageDropdownMenuDisplayed: false,
  language: localStorage.getItem('gateway_language'),
  fontSize: localStorage.getItem('font_size')
    ? Number(localStorage.getItem('font_size'))
    : DEFAULT_FONT_SIZE,
  theme: localStorage.getItem('gateway_theme') || 'auto',
  userDropdownMenuDisplayed: false,
  languageIndex: findLanguageIndex({
    language: StripDoubleQuotes({
      value: localStorage.getItem('gateway_language'),
    }),
  }),
  // notebook tool:
  jupyterBaseURL: '',
  jupyterURL: '',
  jupyterRender: 0,
  xsrfToken: null,
  loadingToken: false,
  notifications: [],
  sitePanelFilter: '',
  projectPanelFilter: '',
  loadingSites: false,
  updateServiceStatus: [],
  gatewayLogs: [],
  projects: [],
  loadingProjects: false,
  showPreferencesModal: false,
};

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const appSlice = createSlice({
  name: 'app',
  initialState: initialState,
  reducers: {
    //
    setShowNewSessionModal: (
      state: AppState,
      action: PayloadAction<{
        show: boolean;
        site_uuid?: string;
        service_uuid?: string;
        service_type?: string;
      }>,
    ) => {
      state.showNewSessionModal = {
        show: action.payload.show,
        site_uuid: action.payload.site_uuid,
        service_uuid: action.payload.service_uuid,
        service_type: action.payload.service_type,
      };
    },
    setSelectedTool: (state: AppState, action: PayloadAction<ToolType>) => {
      state.selectedTool = action.payload;
    },
    setIntermediateTool: (state: AppState, action: PayloadAction<ToolType>) => {
      state.intermediateTool = action.payload;
    },
    setLanguageDropdownMenuDisplayed: (
      state: AppState,
      action: PayloadAction<boolean>,
    ) => {
      state.languageDropdownMenuDisplayed = action.payload;
    },
    toggleLanguageDropdownMenuDisplayed: (state: AppState) => {
      state.languageDropdownMenuDisplayed = !state.languageDropdownMenuDisplayed;
    },
    setLanguage: (state: AppState, action: PayloadAction<string>) => {
      state.language = action.payload;
    },
    setFontSize: (state: AppState, action: PayloadAction<number>) => {
      state.fontSize = action.payload;
      localStorage.setItem('font_size', action.payload.toString());
    },
    setTheme: (state: AppState, action: PayloadAction<string>) => {
      state.theme = action.payload;
      localStorage.setItem('gateway_theme', action.payload);
    },
    setUserDropdownMenuDisplayed: (state: AppState, action: PayloadAction<boolean>) => {
      state.userDropdownMenuDisplayed = action.payload;
    },
    setLanguageIndex: (state, action: PayloadAction<number | undefined>) => {
      state.languageIndex =
        action.payload ??
        findLanguageIndex({
          language: StripDoubleQuotes({
            value: localStorage.getItem('gateway_language'),
          }),
        });
    },
    // notebook tool:
    setJupyterBaseURL: (state: AppState, action: PayloadAction<string>) => {
      state.jupyterBaseURL = action.payload;
    },
    setJupyterURL: (state: AppState, action: PayloadAction<string>) => {
      state.jupyterURL = action.payload;
    },
    setJupyterRender: (state: AppState, action: PayloadAction<number>) => {
      state.jupyterRender = action.payload;
    },
    setXsrfToken: (state: AppState, action: PayloadAction<string | null>) => {
      state.xsrfToken = action.payload;
    },
    setLoadingToken: (state: AppState, action: PayloadAction<boolean>) => {
      state.loadingToken = action.payload;
    },
    setNotifications: (state: AppState, action: PayloadAction<NotificationType[]>) => {
      state.notifications = action.payload;
    },
    addNotification: (state: AppState, action: PayloadAction<NotificationType>) => {
      const existingIndex = state.notifications.findIndex(
        (n) => n.notificationID === action.payload.notificationID,
      );

      // Check that the notification is unique. This is required as we may receive a
      // notification via SSE that was already retrieved with a GET request.
      if (existingIndex === -1) {
        state.notifications.push(action.payload);
        // Ensure the notifications are sorted by date created (newest to oldest).
        state.notifications.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      }
    },
    addNotifications: (state: AppState, action: PayloadAction<NotificationType[]>) => {
      let doSort = false;

      for (var payloadNotification of action.payload) {
        const existingIndex = state.notifications.findIndex(
          (n) => n.notificationID === payloadNotification.notificationID,
        );

        // Check that the notification is unique. This is required as we may receive a
        // notification via SSE that was already retrieved with a GET request.
        if (existingIndex === -1) {
          doSort = true;
          state.notifications.push(payloadNotification);
        }
      }

      if (doSort) {
        // Ensure the notifications are sorted by date created (newest to oldest).
        state.notifications.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      }
    },
    markNotificationsRead: (state: AppState, action: PayloadAction<Number[]>) => {
      state.notifications.forEach((notification) => {
        if (action.payload.includes(notification.notificationID))
          notification.readFlag = true;
      });
    },
    clearNotificationPopupFlags: (state: AppState, action: PayloadAction<Number[]>) => {
      state.notifications.forEach((notification) => {
        if (action.payload.includes(notification.notificationID))
          notification.displayPopup = false;
      });
    },
    setSitePanelFilter: (state: AppState, action: PayloadAction<string>) => {
      state.sitePanelFilter = action.payload;
    },
    setProjectPanelFilter: (state: AppState, action: PayloadAction<string>) => {
      state.projectPanelFilter = action.payload;
    },
    setLoadingSites: (state: AppState, action: PayloadAction<boolean>) => {
      state.loadingSites = action.payload;
    },
    setUpdateServiceStatus: (
      state: AppState,
      action: PayloadAction<ServiceStatusParams[]>,
    ) => {
      state.updateServiceStatus = action.payload;
    },
    setGatewayLogs: (state: AppState, action: PayloadAction<GatewayLogType[]>) => {
      state.gatewayLogs = action.payload;
    },
    prependGatewayLogs: (state: AppState, action: PayloadAction<GatewayLogType>) => {
      for (const log of state.gatewayLogs) {
        if (
          action.payload.message === log.message &&
          action.payload.created_at === log.created_at
        )
          return;
      }
      state.gatewayLogs.unshift(action.payload);
    },
    clearGatewayLogs(state: AppState) {
      state.gatewayLogs = [];
    },
    setProjects: (state: AppState, action: PayloadAction<Project[]>) => {
      state.projects = action.payload;
    },
    setLoadingProjects: (state: AppState, action: PayloadAction<boolean>) => {
      state.loadingProjects = action.payload;
    },
    setShowPreferencesModal: (state: AppState, action: PayloadAction<boolean>) => {
      state.showPreferencesModal = action.payload;
    },
  } /*
  extraReducers: (builder) => {
    builder

      // run some code when the runService action finished, if you need to.
      .addCase(runService.fulfilled, (state: AppState) => {})
  },*/,
});

// expose the interface.
export const {
  setShowNewSessionModal,
  setFontSize,
  setLanguage,
  setLanguageDropdownMenuDisplayed,
  toggleLanguageDropdownMenuDisplayed,
  setLanguageIndex,
  setSelectedTool,
  setIntermediateTool,
  setTheme,
  setUserDropdownMenuDisplayed,
  setJupyterBaseURL,
  setJupyterRender,
  setJupyterURL,
  setXsrfToken,
  setLoadingToken,
  setShowPreferencesModal,
  setNotifications,
  addNotification,
  addNotifications,
  markNotificationsRead,
  clearNotificationPopupFlags,
  setSitePanelFilter,
  setProjectPanelFilter,
  setLoadingSites,
  setUpdateServiceStatus,
  setProjects,
  setLoadingProjects,
  setGatewayLogs,
  prependGatewayLogs,
  clearGatewayLogs,
} = appSlice.actions;
export default appSlice.reducer;
